/**
 * The one copy table of Global Conversations slice 2: every user-visible string of GLOBAL_CONVERSATIONS_SLICE2_SPEC.md
 * §8 for the overlay, the sidebar item, the Projects list chip and the project tab. No string of §8 is written anywhere
 * else in the new `gc/` and `gcs/` code; feature B (the task modal's Conversations) keeps its own default literals.
 * Slice 3 (GLOBAL_CONVERSATIONS_SLICE3_SPEC.md §8) adds its new keys here as well: the modal's "Post to" task choice
 * and Reply button, the overlay's "Complete task…" row, the "Specific task" empty states (F1) and the "Files saved
 * to ‹folder›" line; feature B takes those from this file.
 * Slice 4 (GLOBAL_CONVERSATIONS_SLICE4_SPEC.md §8.1) adds the email strings (the "Client replied" reason, the email
 * card, its footer, the email composer's head line) and the constant-driven variants of the texts that said "comments"
 * while email was hidden: `chipTooltipFor`, `emptyAllHintFor`, `emptyProjectHintFor`, `footnoteProjectFor` and
 * `tabDotTexts` take an explicit `emailUi` flag; today's names (`chipTooltip`, `emptyAllHint`, `emptyProjectHint`,
 * `footnoteProject`, `TAB_DOT_*`, `TAB_ARIA_UNREAD`, `TAB_ARIA_HAS`) delegate with `CONVERSATION_EMAIL_UI` (SD4-20),
 * so they print S2's comment-only text while it is false and the S4 text once it is flipped.
 *
 * Three kinds of export:
 *  - a `const` per fixed string;
 *  - a small pure formatter per string with a number or a name in it (`badgeAria`, `chipTooltip`, `markAllTooltip`,
 *    `newLine`, `earlierReplies`, …);
 *  - for every §8 key marked ‡, a TENANT-LABEL formatter taking `ConvProjectLabels`: only the words
 *    project / projects / Project / Projects take the tenant's label (a tenant labelled "Matters" never sees
 *    "Recent projects"). Callers pass `TenantLabelService` (structurally a `ConvProjectLabels`, the source of
 *    `AppComponentBase.projectLabel*`); with `DEFAULT_CONV_PROJECT_LABELS` each returns the §8 text exactly.
 *
 * Pure: no Angular, no service, no state; the one input it reads is the `CONVERSATION_EMAIL_UI` constant. Curly
 * apostrophes and quotes, the single-character ellipsis and the dashes are exactly as §8 writes them ("Search
 * conversations..." is three dots, "couldn't" a straight apostrophe, and the Add-comment placeholders put no spaces
 * around their em dash).
 *
 * Strings with an `_S4` suffix are the email surfaces' texts, shown only while `CONVERSATION_EMAIL_UI` is true (SD2-15,
 * SD4-20). The email-card, email-footer and email-composer strings carry no suffix: the overlay draws an email entry
 * whenever the server sends one, independent of the constant (SD4-20).
 */
import { CONVERSATION_EMAIL_UI } from './conversation-enums';

// ---------------------------------------------------------------------------------------------------------------------
// Tenant project label
// ---------------------------------------------------------------------------------------------------------------------

/** The tenant's project wording in the four forms the copy needs. `TenantLabelService` satisfies it structurally. */
export interface ConvProjectLabels {
    /** "Project" (Title case, singular). */
    readonly singular: string;
    /** "project" (mid-sentence, singular). */
    readonly singularLower: string;
    /** "Projects" (Title case, plural). */
    readonly plural: string;
    /** "projects" (mid-sentence, plural). */
    readonly pluralLower: string;
}

/** The default wording: with it every ‡ formatter returns the §8 text exactly. For specs and label-less callers. */
export const DEFAULT_CONV_PROJECT_LABELS: ConvProjectLabels = Object.freeze({
    singular: 'Project',
    singularLower: 'project',
    plural: 'Projects',
    pluralLower: 'projects',
});

// ---------------------------------------------------------------------------------------------------------------------
// Sidebar item and badge
// ---------------------------------------------------------------------------------------------------------------------

/** The sidebar item's label and its slim-sidebar tooltip. */
export const NAV_LABEL = 'Conversations';

/** The largest count the badge prints; above it the badge reads `NAV_BADGE_OVERFLOW`. */
export const NAV_BADGE_MAX = 99;

/** The badge text above `NAV_BADGE_MAX`. */
export const NAV_BADGE_OVERFLOW = '99+';

/** The badge text: "‹N›" for 1-99, "99+" above (the caller hides the badge at 0). */
export function badgeText(n: number): string {
    const count = wholeCount(n);

    return count > NAV_BADGE_MAX ? NAV_BADGE_OVERFLOW : `${count}`;
}

/** The badge's aria-label, with the real count (also above 99): "‹N› conversations need your attention". */
export function badgeAria(n: number): string {
    const count = wholeCount(n);

    return `${count} conversations need your attention`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Overlay header and scope selector
// ---------------------------------------------------------------------------------------------------------------------

/** The dialog title. */
export const OVERLAY_TITLE = 'Conversations';

/** The close button's aria-label and tooltip. */
export const OVERLAY_CLOSE = 'Close';

/** The scope button's aria-label: "Conversation scope: ‹label›". */
export function scopeButtonAria(label: string): string {
    return `Conversation scope: ${label || ''}`;
}

/**
 * The scope button's aria-label with its tooltip's text when that text adds to the label (owner 2026-10-21: the tooltip
 * no longer pops on every open's initial focus, so the button's name carries it): "Conversation scope: All projects —
 * Conversations across all your projects". A project's tooltip is its name, the label already: then, and with no
 * tooltip, it is `scopeButtonAria(label)`.
 */
export function scopeButtonAriaWithTip(label: string, tooltip: string): string {
    const name: string = scopeButtonAria(label);
    const tip: string = (tooltip || '').trim();

    return tip === '' || tip === (label || '').trim() ? name : `${name} — ${tip}`;
}

/**
 * ‡ The scope button's tooltip in All scope (in project scope the tooltip is the project's name). Owner 2026-10-21: it
 * shows by itself (on the open's initial focus) only on the user's first All-scope open, as a one-time guide; after
 * that only on hover and on a focus the user moves there (`overlay-scope-hint.ts`).
 */
export function scopeAllTooltip(labels: ConvProjectLabels): string {
    return `Conversations across all your ${labels.pluralLower}`;
}

/** ‡ The All-projects option, and the scope label in All scope: "All projects". */
export function scopeAllProjects(labels: ConvProjectLabels): string {
    return `All ${labels.pluralLower}`;
}

/** ‡ The selector's Recent group title: "Recent projects". */
export function scopeRecentProjects(labels: ConvProjectLabels): string {
    return `Recent ${labels.pluralLower}`;
}

/** ‡ The selector's Favorites group title: "Favorite projects". */
export function scopeFavoriteProjects(labels: ConvProjectLabels): string {
    return `Favorite ${labels.pluralLower}`;
}

/** ‡ The selector's Projects group title (the first projects without a search; the matches while searching). */
export function scopeProjectsGroup(labels: ConvProjectLabels): string {
    return labels.plural;
}

/** ‡ The selector's search placeholder: "Search projects…". */
export function scopeSearchPlaceholder(labels: ConvProjectLabels): string {
    return `Search ${labels.pluralLower}…`;
}

/** ‡ The selector's search aria-label: "Search projects". */
export function scopeSearchAria(labels: ConvProjectLabels): string {
    return `Search ${labels.pluralLower}`;
}

/** ‡ The selector's line when a search matches nothing. */
export function scopeNoMatch(labels: ConvProjectLabels): string {
    return `No ${labels.singularLower} matches that search.`;
}

/** ‡ The selector's line when the user can access no project at all (every group empty). */
export function scopeNoProjects(labels: ConvProjectLabels): string {
    return `No ${labels.pluralLower} to show.`;
}

/** The suffix of a closed project's option. */
export const SCOPE_CLOSED_SUFFIX = 'Closed';

// ---------------------------------------------------------------------------------------------------------------------
// All-scope toolbar
// ---------------------------------------------------------------------------------------------------------------------

/**
 * The attention tab of the All scope (its count follows as `tabCount(n)`): "Needs my attention" (renamed from "Needs
 * attention", owner 2026-10-21). The label only: the tab is still `AllTab` 'attention' and `counts.attention`.
 */
export const ALL_TAB_ATTENTION = 'Needs my attention';

/** The All tab of the All scope (its count follows as `tabCount(n)`). */
export const ALL_TAB_ALL = 'All';

/** A tab's count part: "(‹N›)". */
export function tabCount(n: number): string {
    const count = wholeCount(n);

    return `(${count})`;
}

/** A tab label with its count in one string, for an accessible name: "‹label› (‹N›)", e.g. "All (12)". */
export function tabWithCount(label: string, n: number): string {
    return `${label || ''} ${tabCount(n)}`;
}

/** The All-scope search placeholder (three plain dots, as §8 writes it). */
export const SEARCH_PLACEHOLDER = 'Search conversations...';

/** The All-scope search aria-label. */
export const SEARCH_ARIA = 'Search conversations';

/** The search clear button's aria-label and tooltip. */
export const SEARCH_CLEAR = 'Clear search';

/** The Filter button. */
export const FILTER_BUTTON = 'Filter';

/** The Filter button's tooltip. */
export const FILTER_TOOLTIP = 'Filter conversations';

/** The Mark all read button. */
export const MARK_ALL_READ = 'Mark all read';

/** The Mark all read tooltip: "Mark all ‹N› as read", plus " — includes ‹M› not shown" when M > 0 (SD2-28). */
export function markAllTooltip(n: number, m: number): string {
    const count = wholeCount(n);
    const notShown = wholeCount(m);
    const suffix = notShown > 0 ? ` — includes ${notShown} not shown` : '';

    return `Mark all ${count} as read${suffix}`;
}

/** The Mark all read tooltip while the button is disabled (the badge is 0). */
export const MARK_ALL_NOTHING = 'Nothing to mark read';

/** The Mark-all status row in the dialog, and its announcement: "Marked ‹N› as read". */
export function markedCountAsRead(n: number): string {
    const count = wholeCount(n);

    return `Marked ${count} as read`;
}

/** The Mark-all status row's action. */
export const MARK_ALL_UNDO = 'Undo';

/** The Mark-all status row action's aria-label. */
export const MARK_ALL_UNDO_ARIA = 'Undo mark all read';

/** The notice (and announcement) when the server can no longer honour an Undo (SD2-07). */
export const UNDO_REFUSED = 'This can no longer be undone.';

// ---------------------------------------------------------------------------------------------------------------------
// Filter panel and chips
// ---------------------------------------------------------------------------------------------------------------------

/** Filter group: content type. */
export const FILTER_GROUP_KIND = 'Content type';

/** Filter group: why the user received it. */
export const FILTER_GROUP_WHY = 'Why I received it';

/** Filter group: location (project, client). */
export const FILTER_GROUP_LOCATION = 'Location';

/** Filter group: time. */
export const FILTER_GROUP_TIME = 'Time';

/** S4: the Emails content type (hidden while `CONVERSATION_EMAIL_UI` is false). */
export const FILTER_KIND_EMAILS_S4 = 'Emails';

/** Content type: task comments. */
export const FILTER_KIND_TASK_COMMENTS = 'Task comments';

/**
 * Content type: project updates, the filter panel's option (this §8 key is not ‡: it keeps the word "Project"). Its
 * CHIP is ‡ (§8 Chips row) and takes its text from `chipKindProjectUpdates(labels)` instead.
 */
export const FILTER_KIND_PROJECT_UPDATES = 'Project updates';

/** Why: mentioned me. */
export const FILTER_WHY_MENTIONED = 'Mentioned me';

/** Why: a reply to my comment. */
export const FILTER_WHY_REPLY_TO_ME = 'Reply to my comment';

/** Why: assigned to me. */
export const FILTER_WHY_ASSIGNED = 'Assigned to me';

/**
 * S4: why: a client replied on an email thread (the option and its chip; SD4-26, OQ4-10), placed after "Assigned to
 * me" and offered only while `CONVERSATION_EMAIL_UI` is true.
 */
export const FILTER_WHY_CLIENT_REPLIED = 'Client replied';

/** Why: watching the task. */
export const FILTER_WHY_WATCHING = 'Watching task';

/** Why: a contributor of the task. */
export const FILTER_WHY_CONTRIBUTOR = 'Task contributor';

/** ‡ Location: the project picker's label, "Project". */
export function filterLocationProject(labels: ConvProjectLabels): string {
    return labels.singular;
}

/** Location: the client picker's label. */
export const FILTER_LOCATION_CLIENT = 'Client';

/** ‡ Location: the project picker's search placeholder, "Search projects…". */
export function filterProjectSearchPlaceholder(labels: ConvProjectLabels): string {
    return `Search ${labels.pluralLower}…`;
}

/** Location: the client picker's search placeholder. */
export const FILTER_CLIENT_SEARCH_PLACEHOLDER = 'Search clients…';

/** Location: the client picker's line when a search matches nothing. */
export const FILTER_CLIENT_NO_MATCH = 'No client matches that search.';

/** Time: today. */
export const FILTER_TIME_TODAY = 'Today';

/** Time: the last 7 days. */
export const FILTER_TIME_LAST_7 = 'Last 7 days';

/** Time: the last 30 days. */
export const FILTER_TIME_LAST_30 = 'Last 30 days';

/** Time: a custom range (the bsDaterangepicker). */
export const FILTER_TIME_CUSTOM = 'Custom date range';

/**
 * ‡ The chip of the "Project updates" content-type filter (§8 Chips row: "the option text", through the tenant label),
 * e.g. "Matter updates". The other content-type and why chips use their option const as is (no label word in them).
 */
export function chipKindProjectUpdates(labels: ConvProjectLabels): string {
    return `${labels.singular} updates`;
}

/** ‡ A project filter chip: "Project: ‹name›". */
export function chipProject(labels: ConvProjectLabels, name: string): string {
    return `${labels.singular}: ${name || ''}`;
}

/** A client filter chip: "Client: ‹name›". */
export function chipClient(name: string): string {
    return `Client: ${name || ''}`;
}

/**
 * A custom-range chip, "‹MMM D› – ‹MMM D, YYYY›" (e.g. "Sep 28 – Oct 3, 2026"), from the filter's `YYYY-MM-DD` days.
 * When the two days fall in different years the start carries its year too ("Dec 28, 2025 – Jan 3, 2026"), so the
 * range never reads as backwards. A day that is not `YYYY-MM-DD` is printed as given.
 */
export function customRangeChip(from: string, to: string): string {
    const start = parseDayKey(from);
    const end = parseDayKey(to);
    let startText = '';
    let endText = '';

    if (!start || !end) {
        return `${from || ''} – ${to || ''}`;
    }
    endText = `${MONTHS_SHORT[end.month - 1]} ${end.day}, ${end.year}`;
    startText =
        start.year === end.year
            ? `${MONTHS_SHORT[start.month - 1]} ${start.day}`
            : `${MONTHS_SHORT[start.month - 1]} ${start.day}, ${start.year}`;
    return `${startText} – ${endText}`;
}

/** A chip's remove button aria-label: "Remove filter ‹label›". */
export function chipRemoveAria(label: string): string {
    return `Remove filter ${label || ''}`;
}

/** The chips row's Clear all. */
export const CHIPS_CLEAR_ALL = 'Clear all';

// ---------------------------------------------------------------------------------------------------------------------
// Project toolbar
// ---------------------------------------------------------------------------------------------------------------------

/** The All tab of a project (its count follows as `tabCount(n)`). */
export const PROJECT_TAB_ALL = 'All';

/** The Comments tab of a project (its count follows as `tabCount(n)`). */
export const PROJECT_TAB_COMMENTS = 'Comments';

/** S4: the Emails tab of a project (hidden while `CONVERSATION_EMAIL_UI` is false). */
export const PROJECT_TAB_EMAILS_S4 = 'Emails';

/** The View control's label. */
export const VIEW_LABEL = 'View:';

/** View: Chronological. */
export const VIEW_CHRONOLOGICAL = 'Chronological';

/** View: Group by task. */
export const VIEW_BY_TASK = 'Group by task';

/** The Chronological option's tooltip. */
export const VIEW_CHRONOLOGICAL_TOOLTIP = 'One time-ordered stream, newest first';

/** The Group by task option's tooltip. */
export const VIEW_BY_TASK_TOOLTIP = 'Entries gathered under the task they came from';

/** The View menu's aria-label. */
export const VIEW_MENU_ARIA = 'View';

/** The task filter's All row and its label when no task is chosen. */
export const TASK_FILTER_ALL = 'All tasks';

/** ‡ The task filter's tooltip with no task chosen. */
export function taskFilterAllTooltip(labels: ConvProjectLabels): string {
    return `Showing every task and ${labels.singularLower} update`;
}

/** The task filter's tooltip with one task chosen: "Showing only “‹task›”". */
export function taskFilterOneTooltip(task: string): string {
    return `Showing only “${task || ''}”`;
}

/** The task search's placeholder. */
export const TASK_SEARCH_PLACEHOLDER = 'Search tasks…';

/** The task search's aria-label. */
export const TASK_SEARCH_ARIA = 'Search tasks';

/** The task picker's line when a search matches nothing. */
export const TASK_NO_MATCH = 'No task matches that name.';

/** The suffix of a completed task's option. */
export const TASK_COMPLETED_SUFFIX = 'Completed';

/** The narrow ⋯ menu's aria-label and tooltip. */
export const NARROW_MENU_LABEL = 'View and task options';

/** The narrow ⋯ menu's View group. */
export const NARROW_GROUP_VIEW = 'View';

/** The narrow ⋯ menu's Task group. */
export const NARROW_GROUP_TASK = 'Task';

/** The project toolbar's Add comment button. */
export const PROJECT_ADD_COMMENT = 'Add comment';

/** ‡ The Add comment button's tooltip. */
export function addCommentTooltip(labels: ConvProjectLabels): string {
    return `Post an update or task comment to this ${labels.singularLower}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------------------------------------------------

/** Date heading of today (printed by B's `ConversationDateService.headingLabel`; kept here for specs). */
export const FEED_HEADING_TODAY = 'Today';

/** Date heading of yesterday (printed by B's `ConversationDateService.headingLabel`; kept here for specs). */
export const FEED_HEADING_YESTERDAY = 'Yesterday';

/** ‡ The Group by task header of the project-update group: "Project updates". */
export function groupProjectUpdates(labels: ConvProjectLabels): string {
    return `${labels.singular} updates`;
}

/** A Group by task header's count: "1 item" / "‹N› items". */
export function groupItemCount(n: number): string {
    const count = wholeCount(n);

    return count === 1 ? '1 item' : `${count} items`;
}

/** The notice of a Group by task view capped at 300 entries (SD-28). */
export const FEED_TRUNCATED = 'Showing the newest 300 conversations. Switch to Chronological to see everything.';

/**
 * The "N new" line (SD2-09): new only "1 new conversation" / "‹N› new conversations"; updated only
 * "1 updated conversation" / "‹M› updated conversations"; both "‹N› new · ‹M› updated"; '' when both are 0.
 */
export function newLine(newCount: number, updatedCount: number): string {
    const fresh = wholeCount(newCount);
    const updated = wholeCount(updatedCount);

    if (fresh > 0 && updated > 0) {
        return `${fresh} new · ${updated} updated`;
    }
    if (fresh > 0) {
        return fresh === 1 ? '1 new conversation' : `${fresh} new conversations`;
    }
    if (updated > 0) {
        return updated === 1 ? '1 updated conversation' : `${updated} updated conversations`;
    }
    return '';
}

/** The "N new" line's action. */
export const NEW_LINE_SHOW = 'Show';

/** The skeleton's label (and its announcement). */
export const FEED_LOADING = 'Loading conversations';

/** The paging row. */
export const FEED_LOADING_MORE = 'Loading more conversations';

/** The notice of a failed page load (a straight apostrophe, as §8 writes it). */
export const FEED_LOAD_FAILED = "Conversations couldn't load.";

/** The failed page load's action. */
export const FEED_RETRY = 'Try again';

/** Empty "Needs my attention" tab (no search or filter): title. */
export const EMPTY_ATTENTION_TITLE = 'You’re caught up';

/** Empty "Needs my attention" tab (no search or filter): hint. */
export const EMPTY_ATTENTION_HINT = 'New mentions, replies, and client messages that need you will appear here.';

/** Empty All tab (no search or filter): title. */
export const EMPTY_ALL_TITLE = 'No conversations yet';

/** ‡ Empty All tab (no search or filter): hint. `emptyAllHintFor` with `CONVERSATION_EMAIL_UI` (SD4-20). */
export function emptyAllHint(labels: ConvProjectLabels): string {
    return emptyAllHintFor(CONVERSATION_EMAIL_UI, labels);
}

/**
 * ‡ Empty All tab (no search or filter): hint, for an explicit email setting. With email shown: "Emails and comments
 * across your projects will appear here." (S4 §8.1); without: "Comments across your projects will appear here." (S2).
 */
export function emptyAllHintFor(emailUi: boolean, labels: ConvProjectLabels): string {
    if (emailUi) {
        return `Emails and comments across your ${labels.pluralLower} will appear here.`;
    }
    return `Comments across your ${labels.pluralLower} will appear here.`;
}

/** Empty All scope with a search or filter: title. */
export const EMPTY_SEARCH_TITLE = 'No conversations found';

/** Empty All scope with a search or filter: hint. */
export const EMPTY_SEARCH_HINT = 'Try another search or remove a filter.';

/** Empty project: title. */
export const EMPTY_PROJECT_TITLE = 'No conversations yet';

/** ‡ Empty project: hint. `emptyProjectHintFor` with `CONVERSATION_EMAIL_UI` (SD4-20). */
export function emptyProjectHint(labels: ConvProjectLabels): string {
    return emptyProjectHintFor(CONVERSATION_EMAIL_UI, labels);
}

/**
 * ‡ Empty project: hint, for an explicit email setting. With email shown: "Emails, task comments, and project updates
 * will appear here." (S4 §8.1); without: "Task comments and project updates will appear here." (S2).
 */
export function emptyProjectHintFor(emailUi: boolean, labels: ConvProjectLabels): string {
    if (emailUi) {
        return `Emails, task comments, and ${labels.singularLower} updates will appear here.`;
    }
    return `Task comments and ${labels.singularLower} updates will appear here.`;
}

/** Empty project: the button that opens Add comment. */
export const EMPTY_PROJECT_ACTION = 'Add comment';

/** Empty project with a tab or task filter: title. */
export const EMPTY_FILTERED_TITLE = 'No conversations match these filters.';

/** Empty project with a tab or task filter: hint. */
export const EMPTY_FILTERED_HINT = 'Try another tab or task.';

/** Empty project with a tab or task filter: the button that clears them. */
export const EMPTY_FILTERED_ACTION = 'Clear filters';

/** ‡ A project the user can no longer open (`ProjectAccessible = false`): title. */
export function notAvailableTitle(labels: ConvProjectLabels): string {
    return `This ${labels.singularLower} is no longer available.`;
}

/** A project the user can no longer open: hint. */
export const NOT_AVAILABLE_HINT = 'It may have been deleted, or you no longer have access.';

/** ‡ The All-scope footnote. */
export function footnoteAll(labels: ConvProjectLabels): string {
    return (
        'Only work you can already open appears here. Items stay until you mark them read, reply, or resolve the ' +
        `note comment. Start new comments from the task or ${labels.singularLower}.`
    );
}

/** ‡ The project-scope footnote. `footnoteProjectFor` with `CONVERSATION_EMAIL_UI` (SD4-20). */
export function footnoteProject(labels: ConvProjectLabels): string {
    return footnoteProjectFor(CONVERSATION_EMAIL_UI, labels);
}

/**
 * ‡ The project-scope footnote, for an explicit email setting. With email shown: "You are seeing every comment and
 * email on this project that you have access to." (S4 §8.1); without: "You are seeing every comment on this project
 * that you have access to." (S2).
 */
export function footnoteProjectFor(emailUi: boolean, labels: ConvProjectLabels): string {
    if (emailUi) {
        return `You are seeing every comment and email on this ${labels.singularLower} that you have access to.`;
    }
    return `You are seeing every comment on this ${labels.singularLower} that you have access to.`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------------------------------------------------

/** The breadcrumb's separator (U+203A), printed with a space on each side. */
export const BREADCRUMB_SEPARATOR = '›';

/** A whole breadcrumb as one string, for an accessible name: "‹lead› › ‹tail›". */
export function breadcrumbText(lead: string, tail: string): string {
    return `${lead || ''} ${BREADCRUMB_SEPARATOR} ${tail || ''}`;
}

/** ‡ The breadcrumb's tail of a project update: "‹project› › Project update". */
export function breadcrumbProjectUpdate(labels: ConvProjectLabels): string {
    return `${labels.singular} update`;
}

/** ‡ The breadcrumb's lead of a task with no project and no client: "No project › ‹task›". */
export function breadcrumbNoProject(labels: ConvProjectLabels): string {
    return `No ${labels.singularLower}`;
}

/**
 * The breadcrumb project's tooltip (it opens the project page with its Conversations; owner 2026-10-19, tester
 * findings I-18/PU-6): "Open ‹project› conversations".
 */
export function breadcrumbProjectTooltip(project: string): string {
    return `Open ${project || ''} conversations`;
}

/** The breadcrumb task's tooltip (it opens the task): "Open ‹task›". */
export function breadcrumbTaskTooltip(task: string): string {
    return `Open ${task || ''}`;
}

/** Type chip: a task comment. */
export const TYPE_TASK_COMMENT = 'Task comment';

/** ‡ Type chip: a project update. */
export function typeChipProjectUpdate(labels: ConvProjectLabels): string {
    return `${labels.singular} update`;
}

/** Type chip: a reply. */
export const TYPE_REPLY = 'Reply';

/**
 * The type chip's black tooltip on a task comment in the overlay: "Comment on the task “‹task›”" (owner decision
 * 2026-10-19, tester case C-2; design TrovveCommentsFeed.dc.html :1272). A reply's chip and the task modal's chips have none.
 */
export function typeChipTaskTooltip(task: string): string {
    return `Comment on the task “${task || ''}”`;
}

/**
 * ‡ The type chip's black tooltip on a project update in the overlay: "Posted on the project — not tied to a task" (owner
 * decision 2026-10-19, tester case PU-2's wording).
 */
export function typeChipProjectUpdateTooltip(labels: ConvProjectLabels): string {
    return `Posted on the ${labels.singularLower} — not tied to a task`;
}

/** The project-scope task badge: "Task: ‹task›". */
export function taskBadge(task: string): string {
    return `Task: ${task || ''}`;
}

/** The project-scope task badge's tooltip: "Open ‹task›". */
export function taskBadgeTooltip(task: string): string {
    return `Open ${task || ''}`;
}

/** The tooltip of a note comment's static quote. */
export const QUOTE_TOOLTIP = 'Comment anchored to this passage in the task notes';

/** Footer: Reply. */
export const FOOTER_REPLY = 'Reply';

/** Footer: Resolve. */
export const FOOTER_RESOLVE = 'Resolve';

/** Footer: Resolve's tooltip. */
export const FOOTER_RESOLVE_TOOLTIP = 'Resolve this note comment for everyone — the task is untouched';

/** Footer: Reopen. */
export const FOOTER_REOPEN = 'Reopen';

/** Footer: Reopen's tooltip. */
export const FOOTER_REOPEN_TOOLTIP = 'Reopen this comment';

/** Footer: Mark read. */
export const FOOTER_MARK_READ = 'Mark read';

/** Footer: Mark read's tooltip. */
export const FOOTER_MARK_READ_TOOLTIP = 'Mark this as read for you — the task and comment are untouched';

/** Footer: Mark read's tooltip on an email entry (S4; overlay Mark read never touches Outlook). */
export const FOOTER_MARK_READ_TOOLTIP_EMAIL = 'Mark this as read for you — the task and email are untouched';

/** Footer: the pill after Mark read (2.6 s), followed by `FOOTER_UNDO`. */
export const FOOTER_MARKED_READ = 'Marked as read';

/** Footer: the pill's Undo. */
export const FOOTER_UNDO = 'Undo';

/** Footer: Open task. */
export const FOOTER_OPEN_TASK = 'Open task';

/** Footer: Open task's tooltip. */
export const FOOTER_OPEN_TASK_TOOLTIP = 'Close Conversations and open this task';

/** Footer: Open in Outlook (S4, an email entry; the overlay stays open, SD4-18). */
export const FOOTER_OPEN_OUTLOOK = 'Open in Outlook';

/** Footer: Open in Outlook's tooltip (S4). */
export const FOOTER_OPEN_OUTLOOK_TOOLTIP = 'Open this email in Outlook';

/** ‡ Footer: Open project (project updates). */
export function footerOpenProject(labels: ConvProjectLabels): string {
    return `Open ${labels.singularLower}`;
}

/** ‡ Footer: Open project's tooltip. */
export function footerOpenProjectTooltip(labels: ConvProjectLabels): string {
    return `Close Conversations and open this ${labels.singularLower}`;
}

/** The ⋯ button's aria-label and tooltip. */
export const MORE_ACTIONS = 'More actions';

/** ⋯ Mark unread. */
export const MENU_MARK_UNREAD = 'Mark unread';

/** ⋯ Delete. */
export const MENU_DELETE = 'Delete';

/** ⋯ Complete task… (S3, SD3-24): a task comment of an open task; the single-character ellipsis, as §8 writes it. */
export const MENU_COMPLETE_TASK = 'Complete task…';

/** The "Complete task…" confirm row's question (S3): "Complete “‹task›”?" with curly quotes. */
export function completeTaskConfirm(task: string): string {
    return `Complete “${task || ''}”?`;
}

/** The "Complete task…" confirm row's confirming button (S3). */
export const COMPLETE_TASK_CONFIRM_ACTION = 'Complete';

/** The "Complete task…" confirm row's Cancel (S3). */
export const COMPLETE_TASK_CANCEL = 'Cancel';

/**
 * The line under a project update's files when it uploaded them into the project folder (S3, SD3-32), in both feeds:
 * "Files saved to ‹folder›". The folder name is printed as given; the caller's aria-label adds ", View in folder".
 */
export function filesSavedTo(folderName: string): string {
    return `Files saved to ${folderName || ''}`;
}

/** A thread's collapsed replies (SD2-22): "Show 1 earlier reply" / "Show ‹N› earlier replies". */
export function earlierReplies(n: number): string {
    const count = wholeCount(n);

    return count === 1 ? 'Show 1 earlier reply' : `Show ${count} earlier replies`;
}

/** The card notice when Resolve finds the comment gone (`responseData = false`). */
export const CARD_COMMENT_GONE = 'This comment is no longer available.';

// ---------------------------------------------------------------------------------------------------------------------
// Email card (S4 §7.2, §7.4, §8.1)
// ---------------------------------------------------------------------------------------------------------------------

/**
 * An email entry's collapsed earlier messages (the SD2-22 pattern): "Show 1 earlier message in this thread" /
 * "Show ‹N› earlier messages in this thread".
 */
export function earlierMessages(n: number): string {
    const count = wholeCount(n);

    return count === 1 ? 'Show 1 earlier message in this thread' : `Show ${count} earlier messages in this thread`;
}

/** The email card's link that loads the newest message's full body (preview state). */
export const READ_FULL_EMAIL = 'Read full email';

/** The "Read full email" link's aria-label: "Read the full email from ‹sender›". */
export function readFullEmailAria(sender: string): string {
    return `Read the full email from ${sender || ''}`;
}

/** The email card while the reader loads the thread (the single-character ellipsis). */
export const EMAIL_LOADING = 'Loading email…';

/** The email card's notice when the reader fails (a straight apostrophe); followed by `EMAIL_LOAD_RETRY`. */
export const EMAIL_LOAD_FAILED = "This email couldn't load.";

/** The failed email notice's action (the same words as `FEED_RETRY`); not shown for a final refusal. */
export const EMAIL_LOAD_RETRY = 'Try again';

/** The paperclip's aria-label and tooltip on an email with attachments. */
export const EMAIL_HAS_ATTACHMENTS = 'Has attachments';

/** The notice (and announcement) when Open in Outlook cannot open the email (a straight apostrophe). */
export const OPEN_OUTLOOK_FAILED = "Outlook couldn't open this email.";

// ---------------------------------------------------------------------------------------------------------------------
// Composers
// ---------------------------------------------------------------------------------------------------------------------

/** The reply composer's placeholder. */
export const REPLY_PLACEHOLDER = 'Write a reply — type @ to mention a teammate…';

/** The reply composer's aria-label. */
export const REPLY_ARIA = 'Write a reply';

/** The reply composer's hint on a task comment: "Replying in ‹task›". */
export function replyHintTask(task: string): string {
    return `Replying in ${task || ''}`;
}

/** ‡ The reply composer's hint on a project update: "Replying to a project update in ‹project›". */
export function replyHintProjectUpdate(labels: ConvProjectLabels, project: string): string {
    return `Replying to a ${labels.singularLower} update in ${project || ''}`;
}

/** S4: the email composer's head line (ST §46): "Replying from ‹mailbox› to ‹recipient›". */
export function replyHintEmail(from: string, to: string): string {
    return `Replying from ${from || ''} to ${to || ''}`;
}

/** The reply composer's Cancel. */
export const REPLY_CANCEL = 'Cancel';

/** The reply composer's post button. */
export const REPLY_POST = 'Reply';

/** The task modal entry's Reply button, which opens the reply composer (S3, SD3-09). */
export const REPLY_ACTION = 'Reply';

/** The Reply button's aria-label on a task comment (S3): "Reply to ‹author›'s comment" (a straight apostrophe). */
export function replyActionAria(author: string): string {
    return `Reply to ${author || ''}'s comment`;
}

/** ‡ The Reply button's aria-label on a project update (S3): "Reply to ‹author›'s project update". */
export function replyActionAriaProjectUpdate(labels: ConvProjectLabels, author: string): string {
    return `Reply to ${author || ''}'s ${labels.singularLower} update`;
}

/** Proofread button. */
export const PROOFREAD_LABEL = 'Proofread';

/** Proofread button's tooltip. */
export const PROOFREAD_TOOLTIP = 'Check spelling and grammar';

/** Proofread button while the request runs. */
export const PROOFREAD_CHECKING = 'Checking…';

/** Proofread button when nothing changed (overlay only, 2.4 s). */
export const PROOFREAD_LOOKS_GOOD = 'Looks good';

/** Add comment: the "Post to" label. */
export const POST_TO_LABEL = 'Post to';

/** The task modal's Add comment: the task choice of "Post to" (S3). */
export const POST_TO_THIS_TASK = 'This task';

/** ‡ Add comment: the Project update target. */
export function postToProjectUpdate(labels: ConvProjectLabels): string {
    return `${labels.singular} update`;
}

/** ‡ Add comment: the Project update target's tooltip. */
export function postToProjectUpdateTooltip(labels: ConvProjectLabels, project: string): string {
    return `Post to ${project || ''} — shared context across the ${labels.singularLower}’s tasks`;
}

/** Add comment: the Specific task target. */
export const POST_TO_SPECIFIC_TASK = 'Specific task';

/** Add comment: the Specific task target's tooltip. */
export const POST_TO_SPECIFIC_TASK_TOOLTIP = 'Post on one task only';

/** Add comment: the tooltip of the disabled Specific task target in a project with no tasks (S3, SD3-25). */
export const POST_TO_SPECIFIC_TASK_NONE = 'No tasks in this project';

/** Add comment: the targets' radiogroup aria-label. */
export const POST_TO_ARIA = 'Comment scope';

/** Add comment: the task picker's placeholder. */
export const TASK_CHOOSE_PLACEHOLDER = 'Choose a task…';

/** Add comment: the task picker listbox's aria-label. */
export const TASK_CHOOSE_ARIA = 'Choose a task';

/** Add comment: the task picker's line when there is no search and the project has no task (S3, SD3-25). */
export const TASK_NONE_IN_PROJECT = 'No tasks in this project yet.';

/** ‡ Add comment: the helper under the Project update target. */
export function helperProjectUpdate(labels: ConvProjectLabels): string {
    return `Visible across this ${labels.singularLower} and included in its conversation.`;
}

/** ‡ Add comment: the helper under the Specific task target. */
export function helperSpecificTask(labels: ConvProjectLabels): string {
    return `Appears on the selected task and rolls up to this ${labels.singularLower} conversation.`;
}

/** ‡ Add comment: the placeholder of a project update (no spaces around the em dash). */
export function placeholderProjectUpdate(labels: ConvProjectLabels): string {
    return `Add a ${labels.singularLower} update—type @ to mention a teammate…`;
}

/** Add comment: the placeholder of a task comment (no spaces around the em dash). */
export const ADD_COMMENT_TASK_PLACEHOLDER = 'Add a comment—type @ to mention a teammate…';

/** Add comment: the submit button of a project update. */
export const ADD_COMMENT_SUBMIT_UPDATE = 'Post update';

/** Add comment: the submit button of a task comment. */
export const ADD_COMMENT_SUBMIT_TASK = 'Add comment';

/** Add comment: the submit tooltip of a project update. */
export const ADD_COMMENT_SUBMIT_UPDATE_TOOLTIP = 'Post this update';

/** Add comment: the submit tooltip of a task comment. */
export const ADD_COMMENT_SUBMIT_TASK_TOOLTIP = 'Post this comment';

/** Add comment: the submit tooltip while the composer is empty. */
export const ADD_COMMENT_SUBMIT_EMPTY_TOOLTIP = 'Write something to post';

// ---------------------------------------------------------------------------------------------------------------------
// Announcements (the overlay's live region; also: markedCountAsRead, newLine, UNDO_REFUSED, FEED_LOADING)
// ---------------------------------------------------------------------------------------------------------------------

/** After a page of results: "Showing ‹N› conversations". */
export function showingConversations(n: number): string {
    const count = wholeCount(n);

    return `Showing ${count} conversations`;
}

/** After Mark read. */
export const ANNOUNCE_MARKED_READ = 'Marked as read';

/** After Mark unread. */
export const ANNOUNCE_MARKED_UNREAD = 'Marked as unread';

/** After an Undo. */
export const ANNOUNCE_UNDONE = 'Undone';

/** After Resolve. */
export const ANNOUNCE_RESOLVED = 'Resolved';

/** After Reopen. */
export const ANNOUNCE_REOPENED = 'Reopened';

/** After a reply is posted. */
export const ANNOUNCE_REPLY_SENT = 'Reply sent';

/** After a delete. */
export const ANNOUNCE_COMMENT_DELETED = 'Comment deleted';

/** After a project update is posted (this §8 key is not ‡). */
export const ANNOUNCE_PROJECT_UPDATE_POSTED = 'Project update posted';

/** After a task comment is posted from Add comment. */
export const ANNOUNCE_COMMENT_ADDED = 'Comment added';

/** After "Complete task…" completes the task in place (S3). */
export const ANNOUNCE_TASK_COMPLETED = 'Task completed';

// ---------------------------------------------------------------------------------------------------------------------
// Projects list chip and project tab
// ---------------------------------------------------------------------------------------------------------------------

/**
 * The Projects list chip's tooltip and aria-label: "‹N› comments · ‹U› unread — open the conversation", the unread
 * part only when U > 0, "1 comment" in the singular. `chipTooltipFor` with `CONVERSATION_EMAIL_UI` (SD4-20): it says
 * "comments" while email is hidden (SD2-15) and "comments and emails" once it is shown.
 */
export function chipTooltip(n: number, u: number): string {
    return chipTooltipFor(CONVERSATION_EMAIL_UI, n, u);
}

/**
 * The Projects list chip's tooltip and aria-label for an explicit email setting (ST §25). With email shown:
 * "1 comment or email — open the conversation" / "‹N› comments and emails · ‹U› unread — open the conversation"
 * (S4 §8.1); without: "1 comment" / "‹N› comments" (S2). The unread part only when U > 0, in both.
 */
export function chipTooltipFor(emailUi: boolean, n: number, u: number): string {
    const count = wholeCount(n);
    const unread = wholeCount(u);
    const unreadPart = unread > 0 ? ` · ${unread} unread` : '';
    let items = '';

    if (emailUi) {
        items = count === 1 ? '1 comment or email' : `${count} comments and emails`;
    } else {
        items = count === 1 ? '1 comment' : `${count} comments`;
    }
    return `${items}${unreadPart} — open the conversation`;
}

/** The project page's Conversations tab. */
export const TAB_LABEL = 'Conversations';

/** ‡ The project page tab's tooltip: "Open the whole project’s conversation". */
export function tabTooltip(labels: ConvProjectLabels): string {
    return `Open the whole ${labels.singularLower}’s conversation`;
}

/** S4: the tab's red dot tooltip with email shown. */
export const TAB_DOT_UNREAD_S4 = 'Unread comments or emails';

/** S4: the tab's grey dot tooltip with email shown. */
export const TAB_DOT_HAS_S4 = 'Has comments or emails';

/** S4: the tab's aria-label with unread comments or emails. */
export const TAB_ARIA_UNREAD_S4 = 'Conversations, unread comments or emails';

/** S4: the tab's aria-label with comments or emails, none unread. */
export const TAB_ARIA_HAS_S4 = 'Conversations, has comments or emails';

/** The project tab's dot tooltips and aria-labels for one email setting (`tabDotTexts`). */
export interface ConvTabDotTexts {
    /** The red dot's tooltip: "Unread comments" (S2) / "Unread comments or emails" (S4). */
    readonly dotUnread: string;
    /** The grey dot's tooltip: "Has comments" (S2) / "Has comments or emails" (S4). */
    readonly dotHas: string;
    /** The aria-label with unread entries: "Conversations, unread comments" (S2) / "…comments or emails" (S4). */
    readonly ariaUnread: string;
    /** The aria-label with entries, none unread: "Conversations, has comments" (S2) / "…comments or emails" (S4). */
    readonly ariaHas: string;
}

/**
 * The project tab's dot tooltips and aria-labels for an explicit email setting: the S4 texts (§8.1) when `emailUi` is
 * true, S2's comment-only texts when false. The aria-label with no entries is `TAB_ARIA_NONE` in both. A new object
 * each time: callers may keep it.
 */
export function tabDotTexts(emailUi: boolean): ConvTabDotTexts {
    if (emailUi) {
        return {
            dotUnread: TAB_DOT_UNREAD_S4,
            dotHas: TAB_DOT_HAS_S4,
            ariaUnread: TAB_ARIA_UNREAD_S4,
            ariaHas: TAB_ARIA_HAS_S4,
        };
    }
    return {
        dotUnread: 'Unread comments',
        dotHas: 'Has comments',
        ariaUnread: 'Conversations, unread comments',
        ariaHas: 'Conversations, has comments',
    };
}

/** The tab texts of the current email setting (`CONVERSATION_EMAIL_UI`), read once at load for the four constants. */
const CURRENT_TAB_DOT_TEXTS: ConvTabDotTexts = tabDotTexts(CONVERSATION_EMAIL_UI);

/** The tab's red dot tooltip: `tabDotTexts(CONVERSATION_EMAIL_UI).dotUnread` (SD4-20). */
export const TAB_DOT_UNREAD: string = CURRENT_TAB_DOT_TEXTS.dotUnread;

/** The tab's grey dot tooltip: `tabDotTexts(CONVERSATION_EMAIL_UI).dotHas` (SD4-20). */
export const TAB_DOT_HAS: string = CURRENT_TAB_DOT_TEXTS.dotHas;

/** The tab's aria-label with unread entries: `tabDotTexts(CONVERSATION_EMAIL_UI).ariaUnread` (SD4-20). */
export const TAB_ARIA_UNREAD: string = CURRENT_TAB_DOT_TEXTS.ariaUnread;

/** The tab's aria-label with entries, none unread: `tabDotTexts(CONVERSATION_EMAIL_UI).ariaHas` (SD4-20). */
export const TAB_ARIA_HAS: string = CURRENT_TAB_DOT_TEXTS.ariaHas;

/** The tab's aria-label with no entries (the same with and without email). */
export const TAB_ARIA_NONE = 'Conversations';

// ---------------------------------------------------------------------------------------------------------------------
// Helpers (private)
// ---------------------------------------------------------------------------------------------------------------------

/** English month abbreviations for the custom-range chip ("‹MMM D›"). */
const MONTHS_SHORT: ReadonlyArray<string> = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

/** A `YYYY-MM-DD` day split into numbers. */
interface DayParts {
    year: number;
    month: number;
    day: number;
}

/** A count as a whole number ≥ 0; anything else (null, NaN, negative) is 0. */
function wholeCount(n: number): number {
    return typeof n === 'number' && isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** `YYYY-MM-DD` → its parts; null when the text is not such a day (month 1-12, day 1-31). */
function parseDayKey(dayKey: string): DayParts | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey || '');
    let year = 0;
    let month = 0;
    let day = 0;

    if (!match) {
        return null;
    }
    year = Number(match[1]);
    month = Number(match[2]);
    day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
    }
    return { year, month, day };
}
